import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type { ReactNode } from "react";
import type { Session, User } from "@supabase/supabase-js";
import { supabase } from "@/lib/supabase";
import { sendWelcomeEmail } from "@/lib/email";

export type Role = "user" | "admin" | "super_admin";
export type AccountType = "buyer" | "seller";
export type SellerStatus =
  | "none"
  | "pending"
  | "approved"
  | "suspended"
  | "rejected";

export interface Profile {
  id: string;
  full_name: string | null;
  email: string | null;
  avatar_url: string | null;
  cover_image: string | null;
  username: string | null;
  bio: string | null;
  location: string | null;
  city: string | null;
  province: string | null;
  country: string | null;
  show_location: boolean;
  show_phone: boolean;
  show_business_info: boolean;
  language: string;
  phone: string | null;
  whatsapp: string | null;
  account_type: AccountType;
  role: Role;
  is_active: boolean;
  seller_status: SellerStatus;
  seller_verified: boolean;
  verified_status: string;
  membership_status: string;
  membership_plan: string | null;
  welcome_email_sent: boolean;
  created_at: string | null;
}

export interface SignInResult {
  error: string | null;
  profile: Profile | null;
}

export interface SignUpParams {
  fullName: string;
  email: string;
  password: string;
  accountType: AccountType;
}

export interface SignUpResult {
  error: string | null;
  needsEmailConfirmation: boolean;
}

export interface AuthContextValue {
  user: User | null;
  profile: Profile | null;
  role: Role | null;
  accountType: AccountType | null;
  sellerStatus: SellerStatus | null;
  isActive: boolean;
  isBlocked: boolean;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<SignInResult>;
  signUp: (params: SignUpParams) => Promise<SignUpResult>;
  signOut: () => Promise<void>;
  signInWithGoogle: () => Promise<{ error: string | null }>;
  resetPassword: (email: string) => Promise<{ error: string | null }>;
  refreshProfile: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

const PROFILE_COLUMNS =
  "id, full_name, email, avatar_url, cover_image, username, bio, location, city, province, country, show_location, show_phone, show_business_info, language, phone, whatsapp, account_type, role, is_active, seller_status, seller_verified, verified_status, membership_status, membership_plan, welcome_email_sent, created_at";

const NETWORK_ERROR_MESSAGE =
  "No pudimos conectar con el servidor. Revisa tu conexión a internet e inténtalo de nuevo.";

// Detecta errores de red (no de credenciales) para mostrar un mensaje claro.
function isNetworkError(error: unknown): boolean {
  if (!(error instanceof Error)) return false;
  const msg = error.message?.toLowerCase() ?? "";
  return (
    msg === "failed to fetch" ||
    msg === "networkerror" ||
    msg.includes("failed to fetch") ||
    msg.includes("network") ||
    msg.includes("load failed")
  );
}

function getRedirectPath(): string {
  const basePath = __BASE_PATH__.split("/").filter(Boolean).join("/");
  return basePath ? `/${basePath}` : "";
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [profile, setProfile] = useState<Profile | null>(null);
  const [loading, setLoading] = useState(true);

  const user = session?.user ?? null;

  const fetchProfile = useCallback(
    async (userId: string, googleAvatar?: string | null) => {
      try {
        const { data, error } = await supabase
          .from("profiles")
          .select(PROFILE_COLUMNS)
          .eq("id", userId)
          .maybeSingle();

        if (error) {
          setProfile(null);
          return;
        }

        const current = (data as Profile) ?? null;

        // Sincroniza la foto de Google cuando el usuario no tiene foto propia.
        if (current && !current.avatar_url && googleAvatar) {
          const { data: updated } = await supabase
            .from("profiles")
            .update({ avatar_url: googleAvatar })
            .eq("id", userId)
            .select(PROFILE_COLUMNS)
            .maybeSingle();
          setProfile((updated as Profile) ?? current);
          return;
        }

        setProfile(current);
      } catch {
        setProfile(null);
      }
    },
    []
  );

  useEffect(() => {
    let mounted = true;

    supabase.auth.getSession().then(({ data }) => {
      if (!mounted) return;
      setSession(data.session);
      if (data.session?.user) {
        fetchProfile(
          data.session.user.id,
          data.session.user.user_metadata?.avatar_url
        );
      }
      setLoading(false);
    });

    const { data: authListener } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        setSession(newSession);
        if (newSession?.user) {
          fetchProfile(
            newSession.user.id,
            newSession.user.user_metadata?.avatar_url
          );
        } else {
          setProfile(null);
        }
      }
    );

    return () => {
      mounted = false;
      authListener.subscription.unsubscribe();
    };
  }, [fetchProfile]);

  const signIn = useCallback(
    async (email: string, password: string): Promise<SignInResult> => {
      let data: Awaited<ReturnType<typeof supabase.auth.signInWithPassword>>["data"];
      let error: Awaited<ReturnType<typeof supabase.auth.signInWithPassword>>["error"];
      try {
        const result = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        data = result.data;
        error = result.error;
      } catch (err) {
        return {
          error: isNetworkError(err)
            ? NETWORK_ERROR_MESSAGE
            : "No pudimos iniciar sesión. Inténtalo de nuevo.",
          profile: null,
        };
      }

      if (error) {
        return { error: error.message, profile: null };
      }

      const userId = data.user?.id;
      if (!userId) {
        return { error: "No se pudo autenticar el usuario.", profile: null };
      }

      const { data: profileData, error: profileError } = await supabase
        .from("profiles")
        .select(PROFILE_COLUMNS)
        .eq("id", userId)
        .maybeSingle();

      if (profileError || !profileData) {
        return {
          error:
            profileError?.message ?? "No se encontró el perfil del usuario.",
          profile: null,
        };
      }

      const result = profileData as Profile;
      setProfile(result);

      // Envía el email de bienvenida (idempotente) si aún no fue enviado.
      if (!result.welcome_email_sent) {
        sendWelcomeEmail().catch(() => {
          // Silencioso: el envío es no bloqueante y se reintenta en el próximo login.
        });
      }

      return { error: null, profile: result };
    },
    []
  );

  const signUp = useCallback(
    async ({
      fullName,
      email,
      password,
      accountType,
    }: SignUpParams): Promise<SignUpResult> => {
      // IMPORTANTE: solo enviamos account_type (buyer/seller). NUNCA role.
      // El trigger de la base de datos fuerza role='user' y nunca lee role
      // del metadata, evitando la escalación de privilegios.
      let data: Awaited<ReturnType<typeof supabase.auth.signUp>>["data"];
      let error: Awaited<ReturnType<typeof supabase.auth.signUp>>["error"];
      try {
        const result = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: fullName,
              account_type: accountType,
            },
          },
        });
        data = result.data;
        error = result.error;
      } catch (err) {
        return {
          error: isNetworkError(err)
            ? NETWORK_ERROR_MESSAGE
            : "No pudimos crear tu cuenta. Inténtalo de nuevo.",
          needsEmailConfirmation: false,
        };
      }

      if (error) {
        return { error: error.message, needsEmailConfirmation: false };
      }

      // Si el registro crea sesión directamente (sin confirmación de email),
      // disparamos el email de bienvenida de forma no bloqueante.
      if (data.session) {
        sendWelcomeEmail().catch(() => {
          // Silencioso.
        });
      }

      return {
        error: null,
        needsEmailConfirmation: !data.session,
      };
    },
    []
  );

  const signInWithGoogle = useCallback(async () => {
    try {
      const { error } = await supabase.auth.signInWithOAuth({
        provider: "google",
        options: {
          redirectTo: `${window.location.origin}${getRedirectPath()}/auth/callback`,
        },
      });
      return { error: error ? error.message : null };
    } catch (err) {
      return {
        error: isNetworkError(err)
          ? NETWORK_ERROR_MESSAGE
          : "No pudimos conectar con Google. Inténtalo de nuevo.",
      };
    }
  }, []);

  const resetPassword = useCallback(async (email: string) => {
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}${getRedirectPath()}/reset-password`,
      });
      return { error: error ? error.message : null };
    } catch (err) {
      return {
        error: isNetworkError(err)
          ? NETWORK_ERROR_MESSAGE
          : "No pudimos enviar el correo. Inténtalo de nuevo.",
      };
    }
  }, []);

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
    setProfile(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      profile,
      role: profile?.role ?? null,
      accountType: profile?.account_type ?? null,
      sellerStatus: profile?.seller_status ?? null,
      isActive: profile?.is_active ?? true,
      isBlocked: profile ? !profile.is_active : false,
      loading,
      signIn,
      signUp,
      signOut,
      signInWithGoogle,
      resetPassword,
      refreshProfile: async () => {
        if (session?.user) {
          await fetchProfile(
            session.user.id,
            session.user.user_metadata?.avatar_url
          );
        }
      },
    }),
    [user, profile, loading, signIn, signUp, signOut, signInWithGoogle, resetPassword, fetchProfile, session]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth debe usarse dentro de <AuthProvider>");
  }
  return context;
}